"use client";

import { useCleverTap, CleverTapUserProfile } from "@/context/clevertap-context";
import { useCallback, useEffect } from "react";
import { usePathname } from "next/navigation";

/**
 * Hook for CleverTap user management
 * Use this hook to track user login, update profile, and logout
 */
export const useCleverTapUser = () => {
    const { onUserLogin, updateProfile, requestPushPermission, logout, isInitialized } = useCleverTap();

    return {
        onUserLogin,
        updateProfile,
        requestPushPermission,
        logout,
        isInitialized,
    };
};

/**
 * Hook for CleverTap event tracking
 * Use this hook to track custom events
 */
export const useCleverTapEvents = () => {
    const { cleverTapTrackEvent, isInitialized } = useCleverTap();

    const trackSignUp = useCallback(
        (method: string) => {
            cleverTapTrackEvent("Sign Up", {
                Method: method,
                Date: new Date(),
            });
        },
        [cleverTapTrackEvent]
    );

    const trackLogin = useCallback(
        (method: string) => {
            cleverTapTrackEvent("Login", {
                Method: method,
                Date: new Date(),
            });
        },
        [cleverTapTrackEvent]
    );

    return {
        cleverTapTrackEvent,
        trackSignUp,
        trackLogin,
        isInitialized,
    };
};

/**
 * Hook to automatically track page views
 * Add this to your root layout or specific pages
 */
export const useCleverTapPageView = () => {
    const { cleverTapTrackEvent, isInitialized } = useCleverTap();
    const pathname = usePathname();

    useEffect(() => {
        if (isInitialized && pathname) {
            cleverTapTrackEvent("Page Viewed", {
                "Page Path": pathname,
                "Page URL": typeof window !== "undefined" ? window.location.href : "",
                Date: new Date(),
            });
        }
    }, [isInitialized, pathname, cleverTapTrackEvent]);
};

export type { CleverTapUserProfile };